import { useState } from "react";
import { Settings, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { financeSaveSettings } from "@/lib/financeApi";

export const FinanceSettings = ({ s, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!s) return null;
  const cur = f || {
    credit_unit: s.credit_unit, credits_per_usd: s.credits_per_usd, inr_per_usd: s.inr_per_usd, daily_budget_credits: s.daily_budget_credits ?? "",
    in: s.rates["claude-sonnet-4-6"].input_per_m, out: s.rates["claude-sonnet-4-6"].output_per_m,
  };
  const set = (k, v) => setF({ ...cur, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await financeSaveSettings({
        credit_unit: cur.credit_unit, credits_per_usd: Number(cur.credits_per_usd), inr_per_usd: Number(cur.inr_per_usd),
        daily_budget_credits: cur.daily_budget_credits === "" ? null : Number(cur.daily_budget_credits),
        rates: { "claude-sonnet-4-6": { input_per_m: Number(cur.in), output_per_m: Number(cur.out) } },
      });
      toast.success("Settings saved — usage history re-priced"); setF(null); setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const Field = ({ k, label, step = "any", type = "number" }) => (
    <label className="text-xs text-muted-foreground flex flex-col gap-1">{label}
      <input data-testid={`fin-set-${k}`} type={type} step={step} value={cur[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" />
    </label>
  );
  return (
    <div className="rounded-xl border bg-card" data-testid="finance-settings">
      <button data-testid="finance-settings-toggle" onClick={() => setOpen(!open)} className="w-full p-4 flex items-center justify-between text-sm">
        <span className="font-semibold flex items-center gap-2"><Settings className="h-4 w-4 text-primary" /> Rates & budget settings</span>
        <span className="text-xs text-muted-foreground">1 $ = {s.credits_per_usd} {s.credit_unit} · Claude ${s.rates["claude-sonnet-4-6"].input_per_m}/{s.rates["claude-sonnet-4-6"].output_per_m} per 1M · {s.daily_budget_credits ? `budget ${s.daily_budget_credits}/day` : "no daily budget"}</span>
      </button>
      {open && (
        <div className="p-4 border-t space-y-3">
          <div className="text-xs text-muted-foreground">{s.rate_source}. Changing a rate re-prices the whole usage history so every figure stays consistent.</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field k="credit_unit" label="Credit unit label" type="text" />
            <Field k="credits_per_usd" label="Credits per US$1 (conversion)" />
            <Field k="inr_per_usd" label="₹ per US$1" />
            <Field k="daily_budget_credits" label="Daily operations budget (credits, blank = none)" />
            <Field k="in" label="Claude Sonnet 4.6 input $/1M tokens" />
            <Field k="out" label="Claude Sonnet 4.6 output $/1M tokens" />
          </div>
          <button data-testid="finance-settings-save" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings</button>
        </div>
      )}
    </div>
  );
};
